import React, {
  createContext,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {useIsInLoadedReportContext} from '../../pages/ReportPage/LoadedReportContext';
import {
  type PanelControlsEvent,
  trackPanelControls as trackPanelControlsBase,
} from '../../services/analytics/panelControlsEvents';
import {usePart} from '../../state/views/hooks';
import type * as PanelTypes from '../../state/views/panel/types';
import type {Ref as PanelBankSectionConfigRef} from '../../state/views/panelBankSectionConfig/types';
import {type RunHistoryKeyInfo} from '../../types/run';
import {useDeepEqualValue} from '../../util/hooks';
import {type LayedOutPanel} from '../../util/panelTypes';
import {type OrganizedSettings} from '../PanelBank/types';
import {type PanelPopupMode} from './types';

// TODO - as panels gets refactored some more, consider separating some of these out to a PanelBankContextProvider

interface PanelContextValue {
  isReadOnly?: boolean; // whether user can edit the panel
  isProjectReadOnly?: boolean; // whether user can edit project level resources (e.g. reports)
  panel: LayedOutPanel;
  panelBankSectionConfigRef: PanelBankSectionConfigRef | undefined;
  panelRef: PanelTypes.Ref;
  inheritedSettings?: OrganizedSettings;
  searchQuery?: string;
  panelPopupMode?: PanelPopupMode;
  setPanelPopupMode: Dispatch<SetStateAction<PanelPopupMode | undefined>>;
  trackPanelControls: (event: Omit<PanelControlsEvent, 'page'>) => void;
}

export const PanelContext = createContext<PanelContextValue>({
  panelBankSectionConfigRef: {
    id: '',
    type: 'panel-bank-section-config',
    viewID: '',
  },
  panelRef: {
    id: '',
    type: 'panel',
    viewID: '',
  },
  panel: {} as LayedOutPanel,
  setPanelPopupMode: () => {},
  trackPanelControls: () => {},
});
PanelContext.displayName = 'PanelContext';

interface PanelContextProviderProps {
  children: ReactNode;
  historyKeyInfo?: RunHistoryKeyInfo; // Used in PanelRunsLinePlot
  /**
   * whether user can edit the panel
   * this flag is primarily used to determine if certain actions are available in a
   * published report or draft report
   */
  isReadOnly?: boolean;
  isProjectReadOnly?: boolean; // whether user can edit project level resources (e.g. reports)
  /**
   * The panelbank section that contains this panel; used for actions like move/delete.
   * The section ref is optional because a panel may be rendered outside the context
   * of a section, in which case those actions would not be available.
   */
  panelBankSectionConfigRef?: PanelBankSectionConfigRef;
  panelRef: PanelTypes.Ref;
  inheritedSettings?: OrganizedSettings;
}

/**
 * Panel components are shared between workspaces and reports, so this context provider
 * acts as a bridge between the two. Not all workspace related context providers wrap
 * around report panel grid, so using certain context hooks will result in an error.
 * PanelContextProvider exists to circumvent this,
 */
export const PanelContextProvider: FC<PanelContextProviderProps> = ({
  children,
  inheritedSettings,
  isReadOnly,
  isProjectReadOnly,
  panelBankSectionConfigRef,
  panelRef,
}) => {
  // oxlint-disable-next-line wandb/no-use-part-in-contexts -- TODO: migrate to reselect selectors
  const panel = usePart(panelRef);
  const deepEqualPanel = useDeepEqualValue(panel);
  const [panelPopupMode, setPanelPopupMode] = useState<PanelPopupMode>();

  const isInReport = useIsInLoadedReportContext();
  const trackPanelControls = useCallback(
    (event: Omit<PanelControlsEvent, 'page'>) => {
      trackPanelControlsBase({
        page: isInReport ? 'report' : 'workspace',
        ...event,
      });
    },
    [isInReport]
  );

  const state = useMemo<PanelContextValue>(
    () => ({
      inheritedSettings,
      isProjectReadOnly,
      isReadOnly,
      panel: deepEqualPanel,
      panelBankSectionConfigRef,
      panelPopupMode,
      panelRef,
      setPanelPopupMode,
      trackPanelControls,
    }),
    [
      deepEqualPanel,
      inheritedSettings,
      isReadOnly,
      isProjectReadOnly,
      panelBankSectionConfigRef,
      panelPopupMode,
      panelRef,
      setPanelPopupMode,
      trackPanelControls,
    ]
  );

  return (
    <PanelContext.Provider value={state}>{children} </PanelContext.Provider>
  );
};

export const usePanelContext = (): PanelContextValue => {
  const value = useContext(PanelContext);

  if (value == null) {
    throw new Error(
      'usePanelContext must be used within a PanelContextProvider'
    );
  }

  return value;
};
